import { useCallback, useEffect, useState } from "react";

const INITIAL_SECONDS = 22 * 3600 + 58;

export type TimerUrgency = "nominal" | "warning" | "critical";

export interface CountdownResult {
  display: string;
  urgency: TimerUrgency;
}

/** Interactive controls layered on top of the read-only countdown — used
 *  by the facilitator session timer (RT-AGENDA). Existing read-only
 *  consumers ignore everything past `urgency`. */
export interface CountdownControls extends CountdownResult {
  /** Whether the clock is currently ticking. */
  running: boolean;
  /** Resume ticking. */
  start: () => void;
  /** Halt ticking (the display freezes). */
  pause: () => void;
  /** Flip running ↔ paused. */
  toggle: () => void;
  /** Reset the remaining time to the initial duration (running state kept). */
  reset: () => void;
}

/**
 * Session countdown timer. Counts down from `initialSeconds`, wrapping at
 * midnight. Runs on mount by default, so read-only consumers (e.g. the
 * HudHeader "SESSION ENDS" display) keep their original behaviour with no
 * change. RT-AGENDA adds start/pause/toggle/reset so a facilitator can
 * drive it as a live session clock; the interval only ticks while running.
 */
export const useCountdown = (
  initialSeconds: number = INITIAL_SECONDS,
): CountdownControls => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [running, setRunning] = useState(true);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 86399)), 1000);
    return () => clearInterval(id);
  }, [running]);

  const start = useCallback(() => setRunning(true), []);
  const pause = useCallback(() => setRunning(false), []);
  const toggle = useCallback(() => setRunning((r) => !r), []);
  const reset = useCallback(() => setSeconds(initialSeconds), [initialSeconds]);

  const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  const urgency: TimerUrgency =
    seconds < 900 ? "critical" : seconds < 3600 ? "warning" : "nominal";

  return { display: `${h}:${m}:${s}`, urgency, running, start, pause, toggle, reset };
};
